import { AlertTriangle, Info, X } from "lucide-react";
import { useAnnouncementMutations, useLiveAnnouncements } from "@/lib/admin";
import { cn } from "@/lib/cn";
import { linkify } from "@/lib/linkify";

/**
 * The body as plain text with its line breaks kept, and our own addresses as links.
 * React renders every run as text — an announcement is never HTML.
 */
export function AnnouncementText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <p className={cn("break-words whitespace-pre-line", className)}>
      {linkify(text).map((r, i) =>
        r.href ? (
          <a
            key={i}
            href={r.href}
            target={r.href.startsWith("mailto:") ? undefined : "_blank"}
            rel="noopener noreferrer"
            className="font-medium underline underline-offset-2"
          >
            {r.text}
          </a>
        ) : (
          <span key={i}>{r.text}</span>
        ),
      )}
    </p>
  );
}

/**
 * Home is the one page every employee sees, which makes this the only reliable
 * internal-comms channel in the estate.
 *
 * `info` can be dismissed per user; `warning` cannot — a maintenance notice
 * that can be waved away is a maintenance notice people miss.
 */
export function AnnouncementBanner() {
  const { data: announcements } = useLiveAnnouncements();
  const { dismiss } = useAnnouncementMutations();

  if (!announcements || announcements.length === 0) return null;

  return (
    <div className="mb-6 space-y-2">
      {announcements.map((a) => {
        const warning = a.level === "warning";
        const Icon = warning ? AlertTriangle : Info;
        return (
          <div
            key={a.id}
            role={warning ? "alert" : "status"}
            className={cn(
              "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm",
              warning
                ? "border-warning/30 bg-warning/10 text-warning"
                : "border-border bg-accent text-accent-foreground",
            )}
          >
            <Icon size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              {a.title && <p className="font-semibold">{a.title}</p>}
              <AnnouncementText
                text={a.body}
                className={a.title ? "mt-0.5" : undefined}
              />
            </div>
            {!warning && (
              <button
                type="button"
                onClick={() => dismiss.mutate(a.id)}
                aria-label="Dismiss announcement"
                className="hover:bg-background/50 -m-1 shrink-0 rounded p-1"
              >
                <X size={15} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
